import { assertNever } from "@redotech/util/type";
import { ArrayStringFilterBuilder } from "../advanced-filters/array-string-filter";
import { BooleanFilterBuilder } from "../advanced-filters/boolean-filter";
import { DateFilterBuilder } from "../advanced-filters/date-filter";
import { ExistsFilterBuilder } from "../advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  GenericFilterBuilder,
  isFilterGroup,
} from "../advanced-filters/generic-advanced-filter-data";
import { NumberFilterBuilder } from "../advanced-filters/number-filter";
import { StringFilterBuilder } from "../advanced-filters/string-filter";
import { AdvancedFilterData, FilterGroup } from "../views";

export function getFilterBuilder(
  filter: AdvancedFilterData | undefined,
): GenericFilterBuilder<any, any, any> {
  if (!filter) {
    throw new Error("filter is undefined");
  }

  switch (filter.type) {
    case AdvancedFilterType.DATE:
      return DateFilterBuilder;
    case AdvancedFilterType.ARRAY_TO_STRING:
      return ArrayStringFilterBuilder;
    case AdvancedFilterType.NUMBER:
      return NumberFilterBuilder;
    case AdvancedFilterType.STRING:
      return StringFilterBuilder;
    case AdvancedFilterType.EXISTS:
      return ExistsFilterBuilder;
    case AdvancedFilterType.BOOLEAN:
      return BooleanFilterBuilder;
    default:
      assertNever(filter.type);
  }
}

export function filtersAreEqual(
  filter1: AdvancedFilterData,
  filter2: AdvancedFilterData,
): boolean {
  if (filter1.type !== filter2.type) {
    return false;
  }
  if (filter1.name !== filter2.name) {
    return false;
  }

  const filterBuilder = getFilterBuilder(filter1);
  return (
    filterBuilder.writeToString(filter1 as any) ===
    filterBuilder.writeToString(filter2 as any)
  );
}

export function advancedFiltersAreEqual(
  filters1: AdvancedFilterData[] | undefined,
  filters2: AdvancedFilterData[] | undefined,
): boolean {
  if (!filters1 && !filters2) {
    return true;
  }
  if (!filters1 || !filters2) {
    return false;
  }
  if (filters1.length !== filters2.length) {
    return false;
  }

  for (const [i, element] of filters1.entries()) {
    if (!filtersAreEqual(element, filters2[i])) {
      return false;
    }
  }

  return true;
}

export function isFilterPartial(filter: AdvancedFilterData): boolean {
  const filterBuilder = getFilterBuilder(filter);
  return filterBuilder.isPartial(filter as any);
}

export function isFilterGroupValid(group: FilterGroup | undefined): boolean {
  if (!group) {
    return true;
  }

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      if (!isFilterGroupValid(clause)) {
        return false;
      }
    } else if (isFilterPartial(clause)) {
      return false;
    }
  }

  return true;
}

export function filterGroupsAreEqual(
  group1: FilterGroup | undefined,
  group2: FilterGroup | undefined,
): boolean {
  if (!group1 && !group2) {
    return true;
  }
  if (!group1 || !group2) {
    return false;
  }
  if (group1.operator !== group2.operator) {
    return false;
  }
  if (group1.clauses.length !== group2.clauses.length) {
    return false;
  }

  for (let i = 0; i < group1.clauses.length; i++) {
    const clause1 = group1.clauses[i];
    const clause2 = group2.clauses[i];

    const isGroup1 = isFilterGroup(clause1);
    const isGroup2 = isFilterGroup(clause2);

    if (isGroup1 !== isGroup2) {
      return false;
    }

    if (isGroup1 && isGroup2) {
      if (!filterGroupsAreEqual(clause1, clause2)) {
        return false;
      }
    } else if (!isGroup1 && !isGroup2 && !filtersAreEqual(clause1, clause2)) {
      return false;
    }
  }

  return true;
}
